import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@core/services/auth/auth.service";
import { MenuService } from "@core/services/auth/menu.service";
import { CustomSegmentService } from "@core/services/custom-segment.service";
import { BusinessService } from "@data/services/business.service";
import { ValidateSignatureErrorModalComponent } from "@shared/components/validate-signature-error-modal/validate-signature-error-modal.component";
import { BffErrorResponse } from "@shared/http-responses/bff-error-response";
import { CompleteResponse, Permissions } from "@shared/schemas/complete-login";
import { LoginRequest } from "@shared/schemas/login-request";
import { Permission } from "@shared/schemas/permission";
import { MessageToastService } from "@shared/services/message-toast.service";
import { BsModalRef, BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { NgxRolesService } from "ngx-permissions";
import { Observable, switchMap, of } from "rxjs";

@Component({
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"],
	standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
	constructor(
		private fb: FormBuilder,
		private authService: AuthService,
		private roles: NgxRolesService,
		private router: Router,
		private route: ActivatedRoute,
		private businessService: BusinessService,
		private segment: CustomSegmentService,
		private modalService: BsModalService,
		private messageToastrService: MessageToastService,
		private window: Window,
		private menuService: MenuService
	) {}

	protected form = this.fb.nonNullable.group({
		username: ["", [Validators.required]],
		password: ["", [Validators.required, Validators.minLength(6)]]
	});
	protected modalRef?: BsModalRef;
	protected isLoadingBtnLogin = false;

	private apiKeyFromAsaas: string | null = null;
	protected showWelcomeModal = false;

	ngOnInit(): void {
		this.handleForcedLogin();
		this.registerUserLoggedInEvent();
	}

	ngOnDestroy(): void {
		this.unregisterUserLoggedInEvent();
	}

	private handleForcedLogin(): void {
		this.apiKeyFromAsaas =
			this.route.snapshot.queryParamMap.get("x-api-key") ||
			this.route.snapshot.queryParamMap.get("access-token");
		if (this.apiKeyFromAsaas) {
			this.login();
		}
	}

	private setPermissions(permissions: Permissions): void {
		this.roles.flushRolesAndPermissions();
		this.roles.addRoleWithPermissions("", [
			...permissions.modules,
			...permissions.pages,
			...permissions.actions,
			this.authService.getUserType()
		]);
	}

	private initializeSegmentTracking(): void {
		this.segment.track({
			table: "baseerp_login",
			screen: "Login",
			action: "Redirect Login"
		});
	}

	private userHasNoPermissions(permission: Permission): boolean {
		return (
			permission.modules.length === 0 &&
			this.authService.getUserType() === "USUARIO_GRUPO"
		);
	}

	private loadAndSetBusinessData(): void {
		if (this.authService.getUserSupport()) {
			return;
		}
		this.businessService
			.getBusinessDefault()
			.subscribe((businessDefault) =>
				this.businessService.setBusinessData(businessDefault)
			);
	}

	private openSubscriptionErrorModal(
		modalOptions: ModalOptions
	): BsModalRef<unknown> {
		return this.modalService.show(
			ValidateSignatureErrorModalComponent,
			modalOptions
		);
	}

	private isASubscriptionError(errRes: BffErrorResponse): boolean {
		return (
			!!errRes.error.errorSubscription &&
			!!Object.keys(errRes.error.errorSubscription).length
		);
	}

	private handleSubscriptionError(errRes: BffErrorResponse): void {
		const initialState: ModalOptions = {
			initialState: {
				modalMsg: errRes.error.errorSubscription?.mensagem,
				modalLink: errRes.error.errorSubscription?.linkExterno,
				modalLinkText: errRes.error.errorSubscription?.linkTexto
			},
			class: "modal-dialog-centered"
		};
		this.modalRef = this.openSubscriptionErrorModal(initialState);
	}

	private handleNormalError({ error }: BffErrorResponse): void {
		this.messageToastrService.errorDefaultToast({
			title: error.message.title,
			message: error.message.description
		});
	}

	private decideWhichLoginServiceToUse(
		login: LoginRequest
	): Observable<CompleteResponse> {
		if (this.apiKeyFromAsaas) {
			return this.authService.loginByAsaas(this.apiKeyFromAsaas);
		}
		return this.authService.login(login);
	}

	protected login() {
		this.isLoadingBtnLogin = true;
		this.decideWhichLoginServiceToUse(this.form.getRawValue())
			.pipe(
				switchMap(({ loginResponse, permissions }) => {
					if (this.userHasNoPermissions(permissions)) {
						this.authService.logout().subscribe();
						return of(null);
					}
					this.authService.setSession(loginResponse);
					this.setPermissions(permissions);
					this.initializeSegmentTracking();
					this.loadAndSetBusinessData();

					return this.menuService.loadMenus();
				})
			)
			.subscribe({
				next: (menu) => {
					if (menu) {
						this.menuService.setMenuList(menu);
					}

					if (this.authService.getUserSupport()) {
						void this.router.navigateByUrl("/suporte/v2");
						this.isLoadingBtnLogin = false;
						return;
					}

					this.authService.loginLegacy().subscribe(() => {
						void this.router.navigateByUrl("/home");
						this.isLoadingBtnLogin = false;
					});
				},
				error: (errRes: BffErrorResponse) => {
					this.isLoadingBtnLogin = false;
					if (this.isASubscriptionError(errRes)) {
						this.handleSubscriptionError(errRes);
						return;
					}
					this.handleNormalError(errRes);
				}
			});
	}

	private watchIfUserIsAlreadyLoggedIn = () => {
		if (this.authService.canGoToHome()) {
			void this.router.navigate(["/home"]);
			return;
		}
	};

	private registerUserLoggedInEvent(): void {
		this.window.addEventListener(
			"storage",
			this.watchIfUserIsAlreadyLoggedIn,
			{ passive: true }
		);
	}

	private unregisterUserLoggedInEvent(): void {
		this.window.removeEventListener(
			"storage",
			this.watchIfUserIsAlreadyLoggedIn
		);
	}
}
